/**
 * THE EIGHT FAMILIES, BY NAME (crm-design/spec/motion.md section 2, D4).
 *
 * A component never writes `"motion-rise"` into a className: it names the family, and the
 * name is the only thing that has to survive a rename of the CSS class. The classes
 * themselves live in `ui/motion.css`, each one with the Apple behaviour it draws written
 * above it.
 *
 * WHICH ONE A COMPONENT TAKES is the recipe in `docs/decisions/motion.md` section 2, one
 * block per component family. In one line each:
 *
 *   fade  a surface that arrives without travelling - a dimming layer, a tooltip, the
 *         skeleton handing over to its content
 *   pop   a menu or a popover coming out of the control that presented it
 *   rise  the phone sheet, from the bottom edge it is attached to
 *   grow  the desktop sheet, growing into place over its dimmed parent
 *   row   a row fading into or out of a list, the slot opening with the layout
 *   move  a moving part travelling between two resting places - a thumb, a knob, a row
 *         that has been dragged to a new slot
 *   state a control's own state change - checked, on, off - which is colour at the
 *         SYSTEM duration and not at the pointer tier a hover tint runs on
 *   turn  a disclosure chevron rotating between its two states
 *
 * `press`, `hover`, the loading blend, the charts, the chrome arrival and the navigation
 * are not in this map on purpose: none of them is applied by a component choosing a class.
 * They are rules that already sit on the elements that have them (`.pressable`,
 * Tailwind's `transition-colors`, `useLoadingBlend`, the chart classes, `chrome-enter` and
 * the View Transition rules).
 */
export const MOTION = {
  fade: 'motion-fade',
  pop: 'motion-pop',
  rise: 'motion-rise',
  grow: 'motion-grow',
  row: 'motion-row',
  move: 'motion-move',
  state: 'motion-state',
  turn: 'motion-turn',
} as const;

/** One of the eight class names above. */
export type MotionFamily = (typeof MOTION)[keyof typeof MOTION];
